/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useQuickList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing quick list modal functionality for API Keys.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAuthStore } from '../../../store/auth';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { fetchApiKeys } from '../api';
import { ApiKey } from '../types';

export function useQuickList() {
  const { user } = useAuthStore();
  const { showError } = useNotifications();
  const { t } = useI18n();
  const tenantSlug = user?.tenant?.slug ?? t('apiKeys.defaultTenant');
  const userId = user?.id;

  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListApiKeys, setQuickListApiKeys] = useState<ApiKey[]>([]);
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);

  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) return;

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      // Load first page
      const firstPage = await fetchApiKeys(
        {
          page: 1,
          limit: 100,
          sortBy: 'name',
          sortOrder: 'asc',
        },
        tenantSlug,
        userId,
      );

      let allApiKeys = Array.isArray(firstPage.data) ? firstPage.data : [];

      // If we got 100 results, try to load more pages
      if (allApiKeys.length === 100 && firstPage.pagination) {
        const totalPages = firstPage.pagination.totalPages || Math.ceil((firstPage.pagination.total || 0) / 100);
        for (let page = 2; page <= totalPages && page <= 10; page++) {
          // Limit to 10 pages max (1000 keys)
          const nextPage = await fetchApiKeys(
            {
              page,
              limit: 100,
              sortBy: 'name',
              sortOrder: 'asc',
            },
            tenantSlug,
            userId,
          );
          if (Array.isArray(nextPage.data)) {
            allApiKeys = [...allApiKeys, ...nextPage.data];
          }
        }
      }

      setQuickListApiKeys(allApiKeys);
    } catch (error) {
      showError(error?.message || t('apiKeys.errors.quickListLoad'));
      setQuickListApiKeys([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, [tenantSlug, userId, showError, t]);

  // Load quick list when modal opens
  useEffect(() => {
    if (showQuickList && quickListApiKeys.length === 0 && !quickListLoadingRef.current) {
      loadQuickList();
    }
  }, [showQuickList, quickListApiKeys.length, loadQuickList]);

  // Filter API keys client-side
  const filteredQuickList = useMemo(() => {
    const apiKeys = quickListApiKeys.filter((k) => k.id != null);
    if (!quickListSearch.trim()) {
      return apiKeys;
    }
    const searchLower = quickListSearch.toLowerCase();
    return apiKeys.filter(
      (apiKey) =>
        apiKey.name?.toLowerCase().includes(searchLower) ||
        apiKey.description?.toLowerCase().includes(searchLower) ||
        apiKey.keyPrefix?.toLowerCase().includes(searchLower),
    );
  }, [quickListApiKeys, quickListSearch]);

  return {
    showQuickList,
    setShowQuickList,
    quickListSearch,
    setQuickListSearch,
    filteredQuickList,
    isQuickListLoading,
  };
}

